import { useState, useEffect, useCallback, useLayoutEffect, createContext, useContext, useMemo } from "react";
import {
  THEME_STORAGE_KEY,
  DEFAULT_THEME,
  normalizeTheme,
  getSystemDark,
  applyThemeToDOM,
  readStoredTheme,
} from "../lib/themeBootstrap.js";

/* eslint-disable react-refresh/only-export-components -- ThemeProvider + useTheme hook + default export share one module */

const ThemeContext = createContext(null);

/**
 * Provider — wrap the app once so every useTheme() consumer shares state.
 */
export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_THEME;
    return readStoredTheme();
  });

  const isDark = theme === "dark" || (theme === "system" && getSystemDark());

  const setTheme = useCallback((t) => {
    const normalizedTheme = normalizeTheme(t);
    if (typeof window !== "undefined") {
      localStorage.setItem(THEME_STORAGE_KEY, normalizedTheme);
    }
    setThemeState(normalizedTheme);
  }, []);

  useLayoutEffect(() => {
    applyThemeToDOM(theme);
  }, [theme]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyThemeToDOM("system");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  const value = useMemo(() => ({ theme, setTheme, isDark }), [theme, setTheme, isDark]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook — returns shared theme state from ThemeProvider.
 * Must be used under `<ThemeProvider>` (see App.jsx).
 */
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return ctx;
}

export default useTheme;
